import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { formatDateToISO } from '../utils/formatters';
import { ArrowLeft, Save, Plus, Trash2 } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const BillFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    vendor_id: '',
    bill_date: formatDateToISO(new Date()),
    due_date: '',
    terms: 'net_30',
    description: '',
    notes: '',
    tax_amount: 0
  });

  const [lines, setLines] = useState([
    {
      line_number: 1,
      description: '',
      account_id: '',
      quantity: 1,
      unit_cost: 0,
      amount: 0
    }
  ]);

  const [vendors, setVendors] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchVendorsAndAccounts();
    if (isEdit) {
      fetchBill();
    }
  }, [id]);

  useEffect(() => {
    // Calculate due date based on terms
    if (formData.bill_date && formData.terms) {
      const billDate = new Date(formData.bill_date);
      let daysToAdd = 30;

      switch (formData.terms) {
        case 'due_on_receipt':
          daysToAdd = 0;
          break;
        case 'net_15':
          daysToAdd = 15;
          break;
        case 'net_30':
          daysToAdd = 30;
          break;
        case 'net_60':
          daysToAdd = 60;
          break;
        case 'net_90':
          daysToAdd = 90;
          break;
        default:
          daysToAdd = 30;
      }

      billDate.setDate(billDate.getDate() + daysToAdd);
      setFormData((prev) => ({
        ...prev,
        due_date: formatDateToISO(billDate)
      }));
    }
  }, [formData.bill_date, formData.terms]);

  const fetchVendorsAndAccounts = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const [vendorsRes, accountsRes] = await Promise.all([
        axios.get(`${API_URL}/vendors?is_active=true`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/gl/accounts?is_active=true`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setVendors(vendorsRes.data?.data || []);
      setAccounts(accountsRes.data?.data || []);
    } catch (err) {
      console.error('Failed to fetch vendors and accounts:', err);
      setError(
        'Failed to fetch vendors and accounts: ' + (err.response?.data?.message || err.message)
      );
    }
  };

  const fetchBill = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_URL}/bills/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const bill = response.data?.data;
      setFormData({
        vendor_id: bill.vendor_id,
        bill_date: bill.bill_date?.split('T')[0] || '',
        due_date: bill.due_date?.split('T')[0] || '',
        terms: bill.terms || 'net_30',
        description: bill.description || '',
        notes: bill.notes || '',
        tax_amount: bill.tax_amount || 0
      });

      if (bill.lines && bill.lines.length > 0) {
        setLines(
          bill.lines.map((line) => ({
            line_number: line.line_number,
            description: line.description || '',
            account_id: line.account_id,
            quantity: parseFloat(line.quantity) || 1,
            unit_cost: parseFloat(line.unit_cost) || 0,
            amount: parseFloat(line.amount) || 0
          }))
        );
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch bill');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLineChange = (index, field, value) => {
    const updatedLines = [...lines];
    updatedLines[index][field] = value;

    // Recalculate amount if quantity or unit_cost changes
    if (field === 'quantity' || field === 'unit_cost') {
      const quantity = parseFloat(updatedLines[index].quantity) || 0;
      const unitCost = parseFloat(updatedLines[index].unit_cost) || 0;
      updatedLines[index].amount = quantity * unitCost;
    }

    setLines(updatedLines);
  };

  const addLine = () => {
    setLines([
      ...lines,
      {
        line_number: lines.length + 1,
        description: '',
        account_id: '',
        quantity: 1,
        unit_cost: 0,
        amount: 0
      }
    ]);
  };

  const removeLine = (index) => {
    if (lines.length > 1) {
      const updatedLines = lines.filter((_, i) => i !== index);
      // Renumber lines
      updatedLines.forEach((line, i) => {
        line.line_number = i + 1;
      });
      setLines(updatedLines);
    }
  };

  const calculateSubtotal = () => {
    return lines.reduce((sum, line) => sum + (parseFloat(line.amount) || 0), 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + (parseFloat(formData.tax_amount) || 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    if (!formData.vendor_id) {
      setError('Please select a vendor');
      setLoading(false);
      return;
    }

    if (lines.some((line) => !line.account_id)) {
      setError('Please select an account for all line items');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      const subtotal = calculateSubtotal();
      const totalAmount = calculateTotal();

      const payload = {
        vendor_id: parseInt(formData.vendor_id),
        bill_date: formData.bill_date,
        due_date: formData.due_date,
        terms: formData.terms,
        description: formData.description,
        notes: formData.notes,
        subtotal: subtotal,
        tax_amount: parseFloat(formData.tax_amount) || 0,
        total_amount: totalAmount,
        lines: lines.map((line) => ({
          line_number: line.line_number,
          description: line.description,
          account_id: parseInt(line.account_id),
          quantity: parseFloat(line.quantity) || 1,
          unit_cost: parseFloat(line.unit_cost) || 0,
          amount: parseFloat(line.amount) || 0
        }))
      };

      if (isEdit) {
        await axios.put(`${API_URL}/bills/${id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API_URL}/bills`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      navigate('/accounting/bills');
    } catch (err) {
      console.error('Bill creation error:', err.response?.data);
      setError(err.response?.data?.message || err.message || 'Failed to save bill');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/accounting/bills')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{isEdit ? 'Edit Bill' : 'New Bill'}</h1>
          <p className="text-sm text-gray-500">
            {isEdit ? 'Update bill information' : 'Create a new bill'}
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Bill Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Bill Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vendor *</label>
              <select
                name="vendor_id"
                value={formData.vendor_id}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-amber-500 focus:border-amber-500"
              >
                <option value="">Select a vendor</option>
                {vendors.map((vendor) => (
                  <option key={vendor.id} value={vendor.id}>
                    {vendor.vendor_name} ({vendor.vendor_code})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Terms</label>
              <select
                name="terms"
                value={formData.terms}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-amber-500 focus:border-amber-500"
              >
                <option value="due_on_receipt">Due on Receipt</option>
                <option value="net_15">Net 15</option>
                <option value="net_30">Net 30</option>
                <option value="net_60">Net 60</option>
                <option value="net_90">Net 90</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bill Date *</label>
              <input
                type="date"
                name="bill_date"
                value={formData.bill_date}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date *</label>
              <input
                type="date"
                name="due_date"
                value={formData.due_date}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Brief description of this bill"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Line Items</h2>
            <button
              type="button"
              onClick={addLine}
              className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg text-sm"
            >
              <Plus className="h-4 w-4" />
              Add Line
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    #
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Description
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Account
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    Qty
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    Unit Cost
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                    Amount
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {lines.map((line, index) => (
                  <tr key={index}>
                    <td className="px-3 py-2 text-sm text-gray-500">{line.line_number}</td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={line.description}
                        onChange={(e) => handleLineChange(index, 'description', e.target.value)}
                        placeholder="Item description"
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={line.account_id}
                        onChange={(e) => handleLineChange(index, 'account_id', e.target.value)}
                        required
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                      >
                        <option value="">Select account</option>
                        {accounts.map((account) => (
                          <option key={account.id} value={account.id}>
                            {account.account_code} - {account.account_name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        value={line.quantity}
                        onChange={(e) => handleLineChange(index, 'quantity', e.target.value)}
                        min="0"
                        step="0.01"
                        className="w-20 border border-gray-300 rounded px-2 py-1 text-sm text-right"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        value={line.unit_cost}
                        onChange={(e) => handleLineChange(index, 'unit_cost', e.target.value)}
                        min="0"
                        step="0.01"
                        className="w-28 border border-gray-300 rounded px-2 py-1 text-sm text-right"
                      />
                    </td>
                    <td className="px-3 py-2 text-sm text-right font-medium">
                      {formatCurrency(line.amount)}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => removeLine(index)}
                        disabled={lines.length === 1}
                        className="text-red-600 hover:text-red-800 disabled:text-gray-400 disabled:cursor-not-allowed"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="mt-6 flex justify-end">
            <div className="w-full md:w-80 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium text-gray-900">
                  {formatCurrency(calculateSubtotal())}
                </span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <label className="text-gray-600">Tax:</label>
                <input
                  type="number"
                  name="tax_amount"
                  value={formData.tax_amount}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className="w-32 border border-gray-300 rounded px-2 py-1 text-right"
                />
              </div>
              <div className="flex justify-between text-base font-semibold border-t pt-3">
                <span className="text-gray-900">Total:</span>
                <span className="text-gray-900">{formatCurrency(calculateTotal())}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={4}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-amber-500 focus:border-amber-500"
              placeholder="Add any additional notes about this bill..."
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => navigate('/accounting/bills')}
            className="flex-1 border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Save className="h-5 w-5" />
            {loading ? 'Saving...' : isEdit ? 'Update Bill' : 'Create Bill'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BillFormPage;
